import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const NotesManager = () => {
  const [allNotes, setAllNotes] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Load all notes from localStorage
  useEffect(() => {
    const savedNotes = JSON.parse(localStorage.getItem('gitsawe_personal_notes') || '{}');
    setAllNotes(savedNotes);
  }, []);

  // Get all notes as flat array with metadata
  const getAllNotesFlat = () => {
    const flatNotes = [];
    
    Object.entries(allNotes).forEach(([contentKey, notes]) => {
      const [contentType, contentId] = contentKey.split('_');
      notes.forEach(note => {
        flatNotes.push({
          ...note,
          contentKey,
          contentType,
          contentId
        });
      });
    });

    return flatNotes;
  };

  // Filter and sort notes
  const getFilteredNotes = () => {
    let filtered = getAllNotesFlat();

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(note =>
        note.text.toLowerCase().includes(term) ||
        note.contentTitle?.toLowerCase().includes(term) ||
        note.contentType.toLowerCase().includes(term)
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(note => note.contentType === selectedCategory);
    }

    // Sort notes
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.timestamp) - new Date(a.timestamp);
        case 'oldest':
          return new Date(a.timestamp) - new Date(b.timestamp);
        case 'alphabetical':
          return a.text.localeCompare(b.text);
        case 'category':
          return a.contentType.localeCompare(b.contentType);
        default:
          return 0;
      }
    });

    return filtered;
  };

  // Delete note
  const handleDeleteNote = (noteToDelete) => {
    if (!window.confirm('ይህን ማስታወሻ መሰረዝ ይፈልጋሉ?')) return;

    const updatedAllNotes = { ...allNotes };
    const contentNotes = updatedAllNotes[noteToDelete.contentKey] || [];
    const filteredNotes = contentNotes.filter(note => note.id !== noteToDelete.id);
    
    if (filteredNotes.length === 0) {
      delete updatedAllNotes[noteToDelete.contentKey];
    } else {
      updatedAllNotes[noteToDelete.contentKey] = filteredNotes;
    }

    localStorage.setItem('gitsawe_personal_notes', JSON.stringify(updatedAllNotes));
    setAllNotes(updatedAllNotes);
    toast.success('🗑️ ማስታወሻ ተሰርዟል');
  };

  // Export notes
  const handleExportNotes = () => {
    const notesToExport = getFilteredNotes();
    const exportData = {
      exportDate: new Date().toISOString(),
      totalNotes: notesToExport.length,
      notes: notesToExport.map(note => ({
        text: note.text,
        contentType: note.contentType,
        contentTitle: note.contentTitle,
        timestamp: note.timestamp,
        lastEdited: note.lastEdited
      }))
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `gitsawe-notes-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('📥 ማስታወሻዎች ወደ ፋይል ተላኩ!');
  };

  // Clear all notes
  const handleClearAllNotes = () => {
    if (!window.confirm('ሁሉንም ማስታወሻዎች መሰረዝ ይፈልጋሉ? ይህ እርምጃ መልሰው ማግኘት አይችሉም!')) return;
    
    localStorage.removeItem('gitsawe_personal_notes');
    setAllNotes({});
    toast.success('🗑️ ሁሉም ማስታወሻዎች ተሰርዘዋል');
  };

  // Get category display name
  const getCategoryName = (category) => {
    const categoryNames = {
      'gitsawe': 'ግጻዌ',
      'sinksar': 'ስንክሳር',
      'wudasie': 'ውዳሴ ማርያም',
      'seatat': 'መጽሐፈ ሰአታት',
      'hibuat': 'ኅቡዓት',
      'mahlet': 'ሥርዓተ ማኅሌት',
      'filseta': 'እትአመነኪ ማርያም',
      'bible': 'መጽሐፍ ቅዱስ',
      'books': 'መጻሕፍት',
      'news': 'ዜናዎች'
    };
    return categoryNames[category] || category;
  };

  // Get unique categories
  const getCategories = () => {
    const categories = new Set();
    Object.keys(allNotes).forEach(contentKey => {
      const [contentType] = contentKey.split('_');
      categories.add(contentType);
    });
    return Array.from(categories);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('am-ET', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredNotes = getFilteredNotes();
  const categories = getCategories();
  const totalNotes = getAllNotesFlat().length;

  return (
    <div className="page-container notes-manager-theme">
      {/* Enhanced Page Header */}
      <div className="page-header">
        <h1 className="page-main-title">
          📝 የግል ማስታወሻዎች አስተዳዳሪ
        </h1>
        <div className="page-subtitle">
          Personal Notes Manager - የእርስዎ ማስታወሻዎችን ያስተዳድሩ
        </div>
        <div className="page-description">
          <p className="page-description-main">
            ሁሉንም የግል ማስታወሻዎችዎን በአንድ ቦታ ይመልከቱ፣ ይፈልጉ እና ያስተዳድሩ
          </p>
          <p className="page-description-sub">
            View, search, and manage all your personal notes in one place
          </p>
        </div>
      </div>

      {/* Statistics */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{totalNotes}</div>
            <div className="text-sm text-gray-600">ጠቅላላ ማስታወሻዎች</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{categories.length}</div>
            <div className="text-sm text-gray-600">ምድቦች</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{filteredNotes.length}</div>
            <div className="text-sm text-gray-600">የተመረጡ ማስታወሻዎች</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">
              {Object.keys(allNotes).length}
            </div>
            <div className="text-sm text-gray-600">የይዘት ክፍሎች</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-container">
        <div className="controls-grid">
          {/* Search */}
          <div className="control-group">
            <label className="control-label">🔍 ፈልግ</label>
            <input
              type="text"
              placeholder="በማስታወሻ ይዘት ወይም ርዕስ ፈልግ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="control-input"
            />
          </div>

          {/* Category Filter */}
          <div className="control-group">
            <label className="control-label">📂 ምድብ</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="control-select"
            >
              <option value="all">ሁሉም ምድቦች</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {getCategoryName(category)}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="control-group">
            <label className="control-label">📊 ደርድር</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="control-select"
            >
              <option value="newest">አዲስ ወደ ድሮ</option>
              <option value="oldest">ድሮ ወደ አዲስ</option>
              <option value="alphabetical">በፊደል ቅደም ተከተል</option>
              <option value="category">በምድብ</option>
            </select>
          </div>

          {/* Actions */}
          <div className="control-group">
            <label className="control-label">⚙️ እርምጃዎች</label>
            <div className="flex gap-2">
              <button
                onClick={handleExportNotes}
                disabled={filteredNotes.length === 0}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                📥 ወደ ፋይል ላክ
              </button>
              <button
                onClick={handleClearAllNotes}
                disabled={totalNotes === 0}
                className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🗑️ ሁሉንም ሰርዝ
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notes List */}
      <div className="content-container">
        <div className="content-header">
          <div className="content-icon">📝</div>
          <h2 className="content-title">
            ማስታወሻዎች ({filteredNotes.length})
          </h2>
        </div>

        {filteredNotes.length > 0 ? (
          <div className="space-y-4">
            {filteredNotes.map((note) => (
              <div key={`${note.contentKey}_${note.id}`} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      {getCategoryName(note.contentType)}
                    </span>
                    {note.contentTitle && (
                      <span className="text-sm text-gray-600">
                        {note.contentTitle}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteNote(note)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    🗑️ ሰርዝ
                  </button>
                </div>
                
                <p className="text-gray-800 mb-3 whitespace-pre-wrap leading-relaxed">
                  {note.text}
                </p>
                
                <div className="text-xs text-gray-500">
                  ተጨምሯል: {formatDate(note.timestamp)}
                  {note.lastEdited && (
                    <span className="ml-4">
                      ተስተካክሏል: {formatDate(note.lastEdited)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : totalNotes > 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">ምንም ማስታወሻ አልተገኘም</h3>
            <p className="text-gray-600">የፍለጋ ወይም የምድብ ማጣሪያዎን ይቀይሩ</p>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">ገና ምንም ማስታወሻ የለም</h3>
            <p className="text-gray-600 mb-4">
              በማንኛውም የይዘት ገጽ ላይ ማስታወሻዎችን መጨመር ይጀምሩ
            </p>
            <p className="text-gray-500 text-sm">
              በማንኛውም ገጽ ላይ የማስታወሻ አዝራሩን ይፈልጉ እና የመጀመሪያ ማስታወሻዎን ይጨምሩ
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesManager;